import React from "react";
import "./index.scss";
import { useTranslation } from "react-i18next";
import Messages from "../../../constants/Messages";
import LoadingAnimation from '../../LoadingAnimation/LoadingAnimation'

const RevenueIndicator = ({
	chartData,
	chartDataType,
	isLoading,
	changeToLocalCurrency,
	currency,
}) => {
	const { t, i18n } = useTranslation(["labels", "errors"]);
	const isRTL = i18n.language === 'ar' || i18n.dir() === 'rtl';


	const getKeys = () => {
		if (chartDataType === "awbCount") return { valueKey: "count", percentKey: "countPercent" };
		if (chartDataType === "tonnage") return { valueKey: "tonnage", percentKey: "tonnagePercent" };
		if (chartDataType === "charges" && !changeToLocalCurrency) return { valueKey: "charge", percentKey: "chargePercent" };
		if (chartDataType === "charges" && changeToLocalCurrency) return { valueKey: "localCharge", percentKey: "localChargePercent" };
		return {};
	};

	// const getSortedArray = (data) => {
	// 	if (!Array.isArray(data) || !data) return [];
	// 	const { valueKey, percentKey } = getKeys();
	// 	if (!valueKey || !percentKey) return data ?? [];

	// 	const sortedData = [...data].filter(Boolean).sort((a, b) => {
	// 		const aVal = parseFloat(a?.dataElementValue?.[valueKey] ?? 0);
	// 		const bVal = parseFloat(b?.dataElementValue?.[valueKey] ?? 0);
	// 		if (bVal !== aVal) return bVal - aVal;
	// 		return (a?.dataElementName ?? "").localeCompare(b?.dataElementName ?? "");
	// 	});

	// 	const minPercent = 45, maxPercent = 100;
	// 	const step = sortedData.length > 1 ? (maxPercent - minPercent) / (sortedData.length - 1) : 0;

		
	// 	let result = sortedData.map((d, index) => ({
	// 		...d,
	// 		dataElementValue: {
	// 			...(d?.dataElementValue ?? {}),
	// 			[percentKey]: Math.round(maxPercent - index * step)
	// 		}
	// 	}));

	// 	// Force last element to minPercent if result is not empty
	// 	if (result.length > 0 && result[result.length - 1]?.dataElementValue) {
	// 		result[result.length - 1].dataElementValue[percentKey] = minPercent;
	// 	}

	// 	// Check backwards from last element
	// 	let lastVal = parseFloat(sortedData?.[sortedData.length - 1]?.dataElementValue?.[valueKey] ?? 0);
	// 	for (let i = result.length - 2; i >= 0; i--) {
	// 		const val = parseFloat(sortedData?.[i]?.dataElementValue?.[valueKey] ?? 0);
	// 		if (val === lastVal && result[i]?.dataElementValue) {
	// 			result[i].dataElementValue[percentKey] = minPercent;
	// 		} else {
	// 			break;
	// 		}
	// 	}

	// 	return result;
	// };

		const getSortedArray = (data) => {
	if (!Array.isArray(data) || !data) return [];
	const { valueKey, percentKey } = getKeys();
	if (!valueKey || !percentKey) return data ?? [];

	const sortedData = [...data].filter(Boolean).sort((a, b) => {
		const aVal = parseFloat(a?.dataElementValue?.[valueKey] ?? 0);
		const bVal = parseFloat(b?.dataElementValue?.[valueKey] ?? 0);
		if (bVal !== aVal) return bVal - aVal;
		return (a?.dataElementName ?? "").localeCompare(b?.dataElementName ?? "");
	});

	const maxVal = parseFloat(sortedData?.[0]?.dataElementValue?.[valueKey] ?? 0);
	const minPercent = 45, maxPercent = 100, zeroPercent = 25;

	let result = sortedData.map((d) => {
		const val = parseFloat(d?.dataElementValue?.[valueKey] ?? 0);

		let percent;
		if (val === 0) {
			percent = zeroPercent; 
		} else if (maxVal > 0) {
			const ratio = val / maxVal;
			percent = Math.round(minPercent + ratio * (maxPercent - minPercent));
		} else {
			percent = zeroPercent; 
		}

		return {
			...d,
			dataElementValue: {
				...(d?.dataElementValue ?? {}),
				[percentKey]: percent
			}
		};
	});

	return result;
};





	const getChartValue = (data) => {
		const val = data?.dataElementValue;
		if (chartDataType === "awbCount") {
			return val?.count ? `${val.count} AWB` : "0";
		} else if (chartDataType === "tonnage") {
			return val?.tonnage ? `${val.tonnage} Kg` : "0";
		} else if (chartDataType === "charges" && !changeToLocalCurrency) {
			return val?.charge
				? `${currency} ${currency === "USD" ? val.charge.toFixed(2) : val.charge}`
				: "0";
		} else if (chartDataType === "charges" && changeToLocalCurrency) {
			return val?.localCharge
				? `${currency} ${currency === "USD" ? val.localCharge.toFixed(2) : val.localCharge}`
				: "0";
		}
		return "0";
	};

	const getChartPercentage = (data) => {
		const val = data?.dataElementValue;
		if (chartDataType === "awbCount") {
			return (val?.countPercent ?? 0) + "%";
		} else if (chartDataType === "tonnage") {
			return (val?.tonnagePercent ?? 0) + "%";
		} else if (chartDataType === "charges" && !changeToLocalCurrency) {
			return (val?.chargePercent ?? 0) + "%";
		} else if (chartDataType === "charges" && changeToLocalCurrency) {
			return (val?.localChargePercent ?? 0) + "%";
		}
		return "0%";
	};

	const destinations = getSortedArray(chartData?.destinationData?.destinations?.slice(0, 5));

	return (
		<div className={`top-destinations-chart ${isRTL ? "rtl" : ""}`}>
			<div className="top-destination-text">{t("DASHBOARD.TOP_DEST")}</div>
			<div className="revenue-indicator-panel">
				{isLoading ? (
					<LoadingAnimation />
				) : (
					<>
						{destinations?.map((data, index) => (
							<div className="bar-chart-container" key={index}>
								<div className="bar-row" id={`dest-data-${index}`}>
									<div className="label-data">{data?.dataElementName}</div>
									<div className="bar-value">
										<div
											className="bar-destination"
											style={{ width: getChartPercentage(data) }}
										>
											{getChartValue(data)}
										</div>
									</div>
								</div>
							</div>
						))}
						{(!chartData ||
							(chartData &&
								chartData.destinationData &&
								chartData?.destinationData?.destinations?.length === 0)) &&
							!isLoading && (
								<div className="empty-records m-0 p-0">
									{t(Messages.NO_DATA_AVAILABLE, { ns: "errors" })}
								</div>
							)}
					</>
				)}
			</div>
		</div>
	);
	
	
};

export default RevenueIndicator;
